import React, { useEffect, useState, useCallback } from "react";
import { Search, Loader2, Truck, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useCountry } from "@/lib/country-context";
import { useToast } from "@/hooks/use-toast";
import adminApi from "@/lib/admin-api";

const STATUSES = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Accepted" },
  { key: "preparing", label: "Preparing" },
  { key: "assigned_to_rider", label: "Assigned" },
  { key: "in_transit", label: "In Transit" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  preparing: "bg-orange-100 text-orange-700 border-orange-200",
  assigned_to_rider: "bg-indigo-100 text-indigo-700 border-indigo-200",
  in_transit: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const NEXT_STATUS_OPTIONS = [
  "confirmed", "preparing", "assigned_to_rider", "in_transit", "delivered", "cancelled",
];

export default function AdminOrdersPage() {
  const { formatPrice } = useCountry();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (search) params.search = search;
      const { data } = await adminApi.get("/orders", { params });
      setOrders(data);
    } catch {
      toast({ title: "Couldn't load orders", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [status, search, toast]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-bold">Orders</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Track and manage every order on the platform.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search order ID, restaurant, address..." className="pl-9 h-10"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-orders-search" />
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto no-scrollbar pb-1">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)}
            data-testid={`admin-orders-filter-${s.key || "all"}`}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${
              status === s.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-muted-foreground hover:border-primary hover:text-primary"
            }`}>{s.label}</button>
        ))}
      </div>

      <div className="bg-card border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto opacity-20 mb-3" />
            <p className="font-medium">No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3 font-semibold">Order</th>
                  <th className="text-left p-3 font-semibold">Restaurant</th>
                  <th className="text-left p-3 font-semibold hidden md:table-cell">Rider</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold">Total</th>
                  <th className="text-right p-3 font-semibold hidden md:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} onClick={() => setSelected(o)}
                    className="border-t hover:bg-muted/30 cursor-pointer transition-colors"
                    data-testid={`admin-order-row-${o.id}`}>
                    <td className="p-3 font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</td>
                    <td className="p-3 font-medium">{o.restaurant_name}</td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell">
                      {o.rider_name || <span className="italic">Unassigned</span>}
                    </td>
                    <td className="p-3">
                      <Badge className={`${STATUS_COLOR[o.status] || "bg-muted"} text-[10px] font-semibold border`}>
                        {o.status.replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="p-3 text-right font-semibold">{formatPrice(o.total)}</td>
                    <td className="p-3 text-right text-muted-foreground text-xs hidden md:table-cell">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <OrderDetailDialog
          orderId={selected.id}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function OrderDetailDialog({ orderId, onClose, onChanged }) {
  const { formatPrice } = useCountry();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [riders, setRiders] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [selectedRider, setSelectedRider] = useState("");

  useEffect(() => {
    Promise.all([
      adminApi.get(`/orders/${orderId}`),
      adminApi.get("/riders/available"),
    ]).then(([o, r]) => {
      setData(o.data);
      setRiders(r.data);
      setSelectedRider(o.data.order.rider_id || "");
    });
  }, [orderId]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await adminApi.patch(`/orders/${orderId}`, { status: newStatus });
      toast({ title: `Order ${newStatus.replace(/_/g, " ")}` });
      onChanged();
      onClose();
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const assignRider = async () => {
    if (!selectedRider) return;
    setUpdating(true);
    try {
      await adminApi.patch(`/orders/${orderId}`, { rider_id: selectedRider });
      toast({ title: "Rider assigned" });
      onChanged();
      onClose();
    } catch (e) {
      toast({ title: "Assignment failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  if (!data) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const o = data.order;
  const c = data.customer;
  const subtotal = (o.total || 0) - (o.delivery_fee || 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Order #{o.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
          <div className="flex items-center gap-2 pt-1">
            <Badge className={`${STATUS_COLOR[o.status]} text-xs font-semibold border`}>
              {o.status.replace(/_/g, " ")}
            </Badge>
            <span className="text-xs text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">
              {new Date(o.created_at).toLocaleString("en-GB")}
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-3">
          {/* Customer + Restaurant */}
          <div className="grid grid-cols-2 gap-4">
            <InfoBlock label="Customer">
              <p className="font-semibold">{c ? `${c.first_name} ${c.last_name}` : "—"}</p>
              <p className="text-xs text-muted-foreground">{c?.phone}</p>
              <p className="text-xs text-muted-foreground">{c?.country}</p>
            </InfoBlock>
            <InfoBlock label="Restaurant">
              <p className="font-semibold">{o.restaurant_name}</p>
              <p className="text-xs text-muted-foreground">Payment: {o.payment_method.replace(/_/g, " ")}</p>
            </InfoBlock>
          </div>

          <InfoBlock label="Delivery address">
            <p className="text-sm">{o.delivery_address}</p>
            {o.notes && <p className="text-xs text-muted-foreground mt-1">Notes: {o.notes}</p>}
          </InfoBlock>

          {/* Items */}
          <div className="bg-muted/30 rounded-xl p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Items</p>
            <div className="space-y-1.5">
              {o.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{it.quantity}× {it.menu_item_name}</span>
                  <span className="font-semibold">{formatPrice(it.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Delivery</span><span>{formatPrice(o.delivery_fee || 0)}</span></div>
              <div className="flex justify-between font-bold border-t pt-1.5"><span>Total</span><span>{formatPrice(o.total)}</span></div>
            </div>
          </div>

          {/* Rider assignment */}
          <div className="bg-card border rounded-xl p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" />Rider assignment
            </p>
            {o.rider_name ? (
              <p className="text-sm">Assigned to <span className="font-semibold">{o.rider_name}</span></p>
            ) : (
              <p className="text-sm text-muted-foreground">No rider assigned yet.</p>
            )}
            <div className="flex gap-2 mt-3">
              <select value={selectedRider} onChange={(e) => setSelectedRider(e.target.value)}
                className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="admin-order-rider-select">
                <option value="">— pick an available rider —</option>
                {riders.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name} • {r.vehicle_type} • {r.phone}
                  </option>
                ))}
                {o.rider_id && !riders.some((r) => r.id === o.rider_id) && (
                  <option value={o.rider_id}>Currently: {o.rider_name}</option>
                )}
              </select>
              <Button size="sm" disabled={!selectedRider || updating} onClick={assignRider}
                data-testid="admin-order-assign-rider">
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign"}
              </Button>
            </div>
          </div>

          {/* Status transitions */}
          <div className="bg-card border rounded-xl p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Set status</p>
            <div className="flex flex-wrap gap-2">
              {NEXT_STATUS_OPTIONS.filter((s) => s !== o.status).map((s) => (
                <Button key={s} variant="outline" size="sm" disabled={updating}
                  onClick={() => updateStatus(s)}
                  data-testid={`admin-order-status-${s}`}
                  className={`text-xs ${s === "cancelled" ? "border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground" : ""}`}>
                  {s.replace(/_/g, " ")}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoBlock({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      {children}
    </div>
  );
}
